import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Shield, User, Mail, Lock, Building, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export const MASTER_PASSWORD_MIN_LENGTH = 12;

export default function SetupWizard({ onSetupComplete }) {
  const [firmName, setFirmName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1); // 1: Welcome & Info, 2: Form, 3: Completed

  const handleStart = () => {
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firmName || !adminName || !adminEmail || !password || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    // This wizard only ever creates the initial administrator - the account that can
    // reset every other user's password - so it holds a higher bar than the 8 characters
    // required of ordinary users.
    if (password.length < MASTER_PASSWORD_MIN_LENGTH) {
      setError(`The administrator password must be at least ${MASTER_PASSWORD_MIN_LENGTH} characters long.`);
      return;
    }

    setError("");
    setLoading(true);
    try {
      const user = await base44.setup.initialize({
        firmName,
        adminName,
        adminEmail,
        adminPassword: password
      });
      toast.success("CLS Portal initialized successfully!");
      setStep(3);
      setTimeout(() => {
        onSetupComplete(user);
      }, 2000);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to initialize setup.");
      toast.error(err.message || "Setup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-tr from-slate-950 via-slate-900 to-slate-950 px-4">
      <div className="max-w-lg w-full bg-slate-900/60 border border-slate-800/80 backdrop-blur-2xl rounded-2xl shadow-2xl p-8 transition-all duration-300">
        
        {/* Header Block */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600/10 border border-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/5">
            <Shield className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-100 tracking-tight">CLS Portal Setup Wizard</h1>
          <p className="text-sm text-slate-400 mt-1.5">First-Run Configuration & Account Creation</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3.5 flex items-start gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300 leading-normal">{error}</p>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6 text-slate-300 text-sm leading-relaxed">
            <p>
              Welcome to the **Complete Law Support Local Portal**. It looks like this is the first time the application is running on your network.
            </p>
            <div className="bg-slate-950/40 border border-slate-800/60 rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">What this wizard does:</h3>
              <ul className="list-disc pl-5 space-y-2 text-xs text-slate-400">
                <li>Configures the firm identity metadata for PDF and document headers.</li>
                <li>Generates a unique local system `Installation ID`.</li>
                <li>Creates the master administrator user account.</li>
                <li>Seeds the SQLite database with default lookup structures.</li>
              </ul>
            </div>
            <p className="text-xs text-slate-500">
              Note: All client details and matter databases will remain strictly inside your firm's local server directory (`C:\CLSPortal\`). No external transmissions will occur.
            </p>
            <button
              onClick={handleStart}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 transition-all flex items-center justify-center gap-2 mt-6 cursor-pointer"
            >
              Begin Installation Setup
            </button>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Law Firm Name</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-slate-500" />
                </span>
                <input
                  type="text"
                  required
                  placeholder="E.g., Jarrod Behan Law"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-650 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>
            </div>

            <div className="border-t border-slate-800/80 my-5 pt-4">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Administrator Account Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-450 uppercase tracking-wider mb-2">Full Name</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-slate-500" />
                    </span>
                    <input
                      type="text"
                      required
                      placeholder="Jane Doe"
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-650 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-455 uppercase tracking-wider mb-2">Email Address</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-500" />
                    </span>
                    <input
                      type="email"
                      required
                      placeholder="admin@firmname.local"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-650 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-455 uppercase tracking-wider mb-2">Password</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-slate-500" />
                      </span>
                      <input
                        type="password"
                        required
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-650 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-455 uppercase tracking-wider mb-2">Confirm Password</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-slate-500" />
                      </span>
                      <input
                        type="password"
                        required
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-3 pl-11 pr-4 text-slate-200 placeholder-slate-650 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 transition-all flex items-center justify-center gap-2 mt-6 cursor-pointer"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              ) : (
                "Initialize System"
              )}
            </button>
          </form>
        )}

        {step === 3 && (
          <div className="text-center py-10 space-y-4">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 animate-bounce mx-auto" />
            <h2 className="text-xl font-bold text-slate-100">Setup Completed!</h2>
            <p className="text-sm text-slate-400 leading-normal">
              Seeding database lookup rules and launching system console...
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
